import { CircleCheck, Lightbulb, type LucideIcon, Split } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { shimmerTextSx } from "@intelligems/ig-theme/utils/shimmerTextSx";
import Stack from "@mui/material/Stack";

import type { WorkLogStepKind } from "./work-log";
import { WORK_LOG_PLATE_SIZE } from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/workLogStyles";

/**
 * Figma's three row glyphs, and only these: `ig-icon/split` for an action,
 * `ig-icon/lightbulb` for a thought, `ig-icon/circle-check` for the closing
 * row.
 */
const ROW_ICON: Record<WorkLogStepKind | "done", LucideIcon> = {
  action: Split,
  thought: Lightbulb,
  done: CircleCheck,
};

const MotionStack = motion.create(Stack);

/**
 * Figma "Atlas / Work-log Row" (151:323): a 22px plate on the rail's own
 * Background/Core/Base-light-low — so it masks the rail running behind it —
 * with the 14px glyph centred, 8px before a single Body 2 regular line in
 * Text/Core/Primary. Plate and label share one centre line; the plate sets the
 * row's height.
 *
 * The step still in progress draws a lighter glyph and shimmers its label.
 */
export const WorkLogRow = ({
  kind,
  label,
  active = false,
  animate = false,
}: {
  kind: WorkLogStepKind | "done";
  label: string;
  active?: boolean;
  animate?: boolean;
}) => {
  const Icon = ROW_ICON[kind];
  const reducedMotion = useReducedMotion();
  return (
    <MotionStack
      direction="row"
      alignItems="center"
      gap={2}
      data-testid="work-log-row"
      data-active={active || undefined}
      initial={animate && !reducedMotion ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { duration: 0.24, ease: [0, 0, 0.2, 1] }
      }
      sx={{ minHeight: WORK_LOG_PLATE_SIZE, position: "relative" }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={(theme) => ({
          backgroundColor: theme.igBackgroundColors.baseLightLow,
          borderRadius: theme.spacing(1),
          color: active
            ? IgCoreTextColors.placeholder
            : theme.igIconColors.light,
          flexShrink: 0,
          height: WORK_LOG_PLATE_SIZE,
          overflow: "hidden",
          width: WORK_LOG_PLATE_SIZE,
        })}
      >
        <Icon size={14} aria-hidden />
      </Stack>
      <IgTypography
        variant="body2"
        sx={[
          {
            color: IgCoreTextColors.primary,
            fontWeight: 400,
            lineHeight: "18px",
            minWidth: 0,
          },
          // Figma's in-progress row stops: #8a8a8a → #1a1a1a → #8a8a8a.
          active &&
            shimmerTextSx(
              IgCoreTextColors.placeholder,
              IgCoreTextColors.primary
            ),
        ]}
      >
        {label}
      </IgTypography>
    </MotionStack>
  );
};
